import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { ToThaiLogo } from "@/components/ToThaiLogo";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login, forcePasswordChange } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && !forcePasswordChange) return <Navigate to="/dashboard" replace />;
  if (user && forcePasswordChange) return <Navigate to="/change-password" replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(email.trim().toLowerCase(), password);
      toast.success(`Witaj, ${data.user.full_name}`);
      if (data.force_password_change) {
        navigate("/change-password");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Błąd logowania";
      setError(typeof msg === "string" ? msg : "Nieprawidłowe dane logowania");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex bg-[var(--tt-bg)]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-1 bg-[var(--tt-sidebar-bg)] p-12 text-white relative overflow-hidden">
        <div className="relative z-10 flex flex-col justify-between max-w-md">
          <div>
            <div className="mb-6">
              <ToThaiLogo layout="stack" size="lg" />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/60">Control Center</div>
            <p className="mt-6 text-white/80 leading-relaxed">
              Profesjonalny system kontroli finansowej dla restauracji i studia masażu. Rozliczenia dzienne, vouchery, alerty, audit log
              i pełen ślad każdej operacji.
            </p>
          </div>
          <div className="space-y-3 text-sm text-white/70">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--tt-sidebar-active)]" /> Audit log append-only (DB-trigger)</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--tt-sidebar-active)]" /> Blokada self-approval w backendzie</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--tt-sidebar-active)]" /> Hashowanie Argon2, JWT z rotacją refresh</div>
          </div>
        </div>
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: "radial-gradient(closest-side at 80% 20%, rgba(20,184,166,0.35), transparent 60%), radial-gradient(closest-side at 20% 90%, rgba(212,160,23,0.18), transparent 60%)",
          }}
        />
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <Card className="tt-card w-full max-w-md">
          <CardHeader>
            {/* Mobile-only brand (desktop sees the hero panel) */}
            <div className="lg:hidden flex justify-center mb-4">
              <div className="w-28"><ToThaiLogo layout="stack" size="sm" /></div>
            </div>
            <CardTitle className="text-2xl tt-display">Zaloguj się</CardTitle>
            <CardDescription>Pełny dostęp do panelu kontroli TO THAI</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="owner@tothai.local"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Hasło</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Minimum 10 znaków"
                    className="pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--tt-muted)] hover:text-[var(--tt-text)]"
                    aria-label="Show password"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription data-testid="login-error">{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Zaloguj
              </Button>
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-text)] underline-offset-4 hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Resetuj hasło
                </Link>
              </div>
            </form>
            {process.env.REACT_APP_SHOW_DEV_CREDENTIALS === "true" && (
              <div className="mt-6 text-xs text-[var(--tt-muted)]" data-testid="dev-credentials-hint">
                Dane testowe (dev only):<br />
                <span className="tt-mono">owner@tothai.local / Owner123!@#</span><br />
                <span className="tt-mono">manager@tothai.local / Manager123!@#</span><br />
                <span className="tt-mono">kelnerka / barmanka / recepcja @tothai.local: Test1234!@#</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
